import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Divider,
  Chip,
  Alert,
  Grid
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  CreditCard as CardIcon,
  AccountBalance as TransferIcon,
  CheckCircle as CheckIcon,
  Receipt as ReceiptIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';

const currencyRD = (v) =>
  new Intl.NumberFormat("es-DO", { style: "currency", currency: "DOP" }).format(v);

const PAYMENT_METHODS = [
  {
    value: 'efectivo',
    label: 'Efectivo',
    icon: <MoneyIcon color="success" />,
    description: 'Pago en efectivo',
    color: 'success'
  },
  {
    value: 'tarjeta',
    label: 'Tarjeta',
    icon: <CardIcon color="primary" />,
    description: 'Tarjeta de crédito/débito',
    color: 'primary'
  },
  {
    value: 'transferencia',
    label: 'Transferencia',
    icon: <TransferIcon color="info" />,
    description: 'Transferencia bancaria',
    color: 'info'
  }
];

export default function MetodoDePagoDialog({ 
  open, 
  onClose, 
  onConfirm, 
  amount = 0, 
  title = "Método de Pago",
  description = "Seleccione el método de pago",
  showAmount = true,
  allowMultiple = true,
  defaultMethod = 'efectivo'
}) {
  const [selectedMethod, setSelectedMethod] = useState(defaultMethod);
  const [montoInput, setMontoInput] = useState(amount || 0);
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [combinedPayments, setCombinedPayments] = useState([]);
  const [remainingAmount, setRemainingAmount] = useState(amount || 0);

  const handleConfirm = () => {
    if (allowMultiple && combinedPayments.length === 0) {
      setError('Por favor agregue al menos un método de pago');
      return;
    }

    if (!allowMultiple) {
      if (!selectedMethod) {
        setError('Por favor seleccione un método de pago');
        return;
      }

      // Validate reference for non-cash payments
      if (selectedMethod !== 'efectivo' && !reference.trim()) {
        setError('Por favor ingrese la referencia del pago');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      let paymentData;

      if (allowMultiple) {
        // Validate combined payments total
        const totalCombined = combinedPayments.reduce((sum, payment) => sum + parseFloat(payment.amount), 0);
        if (Math.abs(totalCombined - amount) > 0.01) {
          setError('El total de los pagos combinados debe ser igual al monto total');
          setLoading(false);
          return;
        }

        paymentData = {
          combined: true,
          payments: combinedPayments,
          totalAmount: amount,
          timestamp: new Date().toISOString()
        };
      }
       else {
        paymentData = {
          method: selectedMethod,
          amount: amount,
          reference: reference.trim(),
          timestamp: new Date().toISOString()
        };
      }
      onConfirm(paymentData);
      handleClose();
    } catch (error) {
      setError('Error al procesar el pago. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedMethod(defaultMethod);
    setReference('');
    setError('');
    setLoading(false);
    setCombinedPayments([]);
    setRemainingAmount(amount || 0);
    onClose();
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !loading) {
      handleConfirm();
    }
  };

  const handleAddCombinedPayment = () => {
    if (!selectedMethod) {
      setError('Por favor seleccione un método de pago');
      return;
    }

    if (selectedMethod !== 'efectivo' && !reference.trim()) {
      setError('Por favor ingrese la referencia del pago');
      return;
    }

    const paymentAmount = parseFloat(montoInput.replace(/,/g, "")) || remainingAmount;
    
    if (paymentAmount <= 0) {
      setError('El monto debe ser mayor a 0');
      return;
    }

    if (paymentAmount > remainingAmount) {
      setError('El monto no puede ser mayor al monto restante');
      return;
    }


    const newPayment = {
      id: Date.now(),
      method: selectedMethod,
      amount: paymentAmount,
      reference: selectedMethod !== 'efectivo' ? reference.trim() : '',
      timestamp: new Date().toISOString()
    };

    setCombinedPayments([...combinedPayments, newPayment]);
    setRemainingAmount(remainingAmount - paymentAmount);
    setSelectedMethod('efectivo');
    setReference('');
    setError('');
    setMontoInput('');
  };

  const handleRemoveCombinedPayment = (paymentId) => {
    const paymentToRemove = combinedPayments.find(p => p.id === paymentId);
    setCombinedPayments(combinedPayments.filter(p => p.id !== paymentId));
    setRemainingAmount(remainingAmount + paymentToRemove.amount);
  };

  const handleCombinedAmountChange = (e) => {
   // const value = parseFloat(e.target.value.replace(/,/g, "")) ;
    setMontoInput(e.target.value);
  };

  const selectedMethodData = PAYMENT_METHODS.find(method => method.value === selectedMethod);

  // Update remaining amount when amount prop changes
  useEffect(() => {
    if (allowMultiple) {
      const totalCombined = combinedPayments.reduce((sum, payment) => sum + payment.amount, 0);
      setRemainingAmount(amount - totalCombined);
    } else {
      setRemainingAmount(amount || 0);
    }
  }, [amount, allowMultiple, combinedPayments]);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      maxHeight="90vh"
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: 400,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <ReceiptIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ 
        pt: 2, 
        overflowY: 'auto', 
        maxHeight: 'calc(90vh - 200px)', 
        pr: 1,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#c1c1c1',
          borderRadius: '4px',
          '&:hover': {
            background: '#a8a8a8',
          },
        },
      }}>
        <Stack spacing={3}>
          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {/* Amount Display */}
          {showAmount && amount && (
            <Box sx={{ 
              bgcolor: 'primary.50', 
              p: 3, 
              borderRadius: 2, 
              border: '1px solid',
              borderColor: 'primary.200',
              textAlign: 'center'
            }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Monto a Pagar
              </Typography>
              <Typography variant="h4" fontWeight={700} color="primary.main">
                {currencyRD(amount)}
              </Typography>
              {allowMultiple && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Restante: {remainingAmount > 0 ? currencyRD(remainingAmount) : 'RD$0.00'}
                </Typography>
              )}
            </Box>
          )}

          {/* Payment Methods Selection */}
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {allowMultiple ? 'Agregar Método de Pago' : 'Seleccione el Método de Pago'}
            </Typography>
            
            <RadioGroup
              value={selectedMethod}
              onChange={(e) => setSelectedMethod(e.target.value)}
            >
              <Stack spacing={2}>
                <Grid container spacing={2}>
                {PAYMENT_METHODS.map((method) => (
                  <Grid item size={4} key={method.value}>
                  <Box
                    key={method.value}
                    sx={{
                      border: '2px solid',
                      borderColor: selectedMethod === method.value ? `${method.color}.main` : 'grey.200',
                      borderRadius: 2,
                      p: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      bgcolor: selectedMethod === method.value ? `${method.color}.50` : 'transparent',
                      '&:hover': {
                        borderColor: `${method.color}.main`,
                        bgcolor: `${method.color}.25`
                      }
                    }}
                    onClick={() => setSelectedMethod(method.value)}
                  >
                    <FormControlLabel
                      value={method.value}
                      control={<Radio color={method.color} />}
                      label={
                        <Stack direction="row" alignItems="center" spacing={2}>
                          {method.icon}
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {method.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {method.description}
                            </Typography>
                          </Box>
                        </Stack>
                      }
                      sx={{ 
                        width: '100%', 
                        margin: 0,
                        '& .MuiFormControlLabel-label': {
                          width: '100%'
                        }
                      }}
                    />
                  </Box>
                  </Grid>
                ))}
                </Grid>
              </Stack>
            </RadioGroup>
          </Box>

          {/* Combined Payments Section */}
          {allowMultiple && (
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Pagos Combinados
              </Typography>
              
              {combinedPayments.length > 0 && (
                <Box sx={{ 
                  mb: 2, 
                  maxHeight: '200px', 
                  overflowY: 'auto', 
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f5f5f5',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#d0d0d0',
                    borderRadius: '3px',
                    '&:hover': {
                      background: '#b0b0b0',
                    },
                  },
                }}>
                  <Stack spacing={1}>
                    {combinedPayments.map((payment) => {
                      const methodData = PAYMENT_METHODS.find(m => m.value === payment.method);
                      return (
                        <Box
                          key={payment.id}
                          sx={{
                            border: '1px solid',
                            borderColor: 'grey.200',
                            borderRadius: 2,
                            p: 2,
                            bgcolor: 'grey.50'
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              {methodData?.icon}
                              <Box>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {methodData?.label}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {formatNumberWithCommas(payment.amount)}
                                  {payment.reference && ` - ${payment.reference}`}
                                </Typography>
                              </Box>
                            </Stack>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleRemoveCombinedPayment(payment.id)}
                              disabled={loading}
                            >
                              Quitar
                            </Button>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {/* Add Payment Section */}
              {remainingAmount > 0 && (
                <Box sx={{ 
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  p: 2,
                  bgcolor: 'primary.25'
                }}>
                  <Box >
                    <Typography variant="subtitle2" fontWeight={600} color="primary.main">
                      Agregar Pago ({currencyRD(remainingAmount)} restante)
                    </Typography>
                    
                    {/* Amount Input for Combined Payments */}
                    <Typography variant="body2" color="text.secondary" sx={{fontWeight:600,ml:1,mt:2}}>Monto</Typography>
                    <TextField
                      fullWidth
                      placeholder="0.00"
                      value={formatNumberWithCommas(montoInput)}
                      onChange={handleCombinedAmountChange}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !loading) {
                          handleAddCombinedPayment();
                        }
                      }}
                      variant="outlined"
                      size="small"
                      InputProps={{
                        startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>RD$</Typography>
                      }}
                    />

                    {/* Reference Input for Non-Cash Payments */}
                    {selectedMethod !== 'efectivo' && (
                      <>
                      <Typography variant="body2" color="text.secondary" sx={{fontWeight:600,ml:1,mt:2}}>Referencia</Typography>
                      <TextField
                        fullWidth
                        placeholder={
                          selectedMethod === 'tarjeta' 
                            ? 'Ej: AUTH123456789' 
                            : 'Ej: TRANSFER-2024-001'
                        }
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        variant="outlined"
                        size="small"
                      />
                      </>
                    )}

                    <Button
                      sx={{mt:2}}
                      variant="outlined"
                      color="primary"
                      onClick={handleAddCombinedPayment}
                      disabled={loading || !selectedMethod || (selectedMethod !== 'efectivo' && !reference.trim())}
                      startIcon={<AddIcon />}
                    >
                      Agregar Pago
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Reference Input for Non-Cash Payments (Single Payment Mode) */}
          {!allowMultiple && selectedMethod !== 'efectivo' && (
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Referencia del Pago
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedMethod === 'tarjeta' 
                  ? 'Ingrese el número de autorización o referencia de la transacción'
                  : 'Ingrese el número de referencia de la transferencia'
                }
              </Typography>
              
              <TextField
                fullWidth
                placeholder={
                  selectedMethod === 'tarjeta' 
                    ? 'Ej: AUTH123456789' 
                    : 'Ej: TRANSFER-2024-001'
                }
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                onKeyPress={handleKeyPress}
                variant="outlined"
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1rem'
                  }
                }}
              />
            </Box>
          )}

          {/* Payment Summary */}
          {(selectedMethod || (allowMultiple && combinedPayments.length > 0)) && (
            <Box sx={{ 
              bgcolor: 'grey.50', 
              p: 3, 
              borderRadius: 2, 
              border: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Resumen del Pago
              </Typography>
              
              <Stack spacing={2}>
                {allowMultiple ? (
                  // Combined payments summary
                  <>
                    {combinedPayments.map((payment, index) => {
                      const methodData = PAYMENT_METHODS.find(m => m.value === payment.method);
                      return (
                        <Stack key={payment.id} direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Chip
                              icon={methodData?.icon}
                              label={methodData?.label}
                              color={methodData?.color}
                              variant="filled"
                              size="small"
                            />
                            {payment.reference && (
                              <Typography variant="body2" color="text.secondary">
                                ({payment.reference})
                              </Typography>
                            )}
                          </Stack>
                          <Typography variant="body2" fontWeight={600}>
                            {formatNumberWithCommas(payment.amount)}
                          </Typography>
                        </Stack>
                      );
                    })}
                    
                    <Divider />
                    
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" fontWeight={600}>
                        Total
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="primary.main">
                        {currencyRD(amount)}
                      </Typography>
                    </Stack>
                  </>
                ) : (
                  // Single payment summary
                  <>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Método de Pago
                      </Typography>
                      <Chip
                        icon={selectedMethodData?.icon}
                        label={selectedMethodData?.label}
                        color={selectedMethodData?.color}
                        variant="filled"
                        size="small"
                      />
                    </Stack>
                    
                    {showAmount && amount && (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Monto
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="primary.main">
                          {currencyRD(amount)}
                        </Typography>
                      </Stack>
                    )}
                    
                    {selectedMethod !== 'efectivo' && reference && (
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Referencia
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {reference}
                        </Typography>
                      </Stack>
                    )}
                  </>
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="primarybutton"
          onClick={handleConfirm}
          disabled={loading || (allowMultiple ? combinedPayments.length === 0 : !selectedMethod) || remainingAmount > 0}
          startIcon={loading ? null : <CheckIcon />}
          sx={{ 
            minWidth: 120,
            fontWeight: 600
          }}
        >
          {loading ? 'Procesando...' : 'Confirmar Pago'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
